"use client";

import { useEffect } from "react";
import { useSearchParams, usePathname } from "next/navigation";

declare global {
  interface Window {
    googleTranslateElementInit?: () => void;
    google?: {
      translate: {
        TranslateElement: new (
          options: {
            pageLanguage: string;
            includedLanguages: string;
            autoDisplay: boolean;
          },
          elementId: string
        ) => void;
      };
    };
  }
}

export const SUPPORTED_LANGUAGES = [
  {
    code: "en",
    shortCode: "EN",
    name: "English",
    englishName: "English",
  },
  {
    code: "fr",
    shortCode: "FR",
    name: "Français",
    englishName: "French",
  },
  {
    code: "it",
    shortCode: "IT",
    name: "Italiano",
    englishName: "Italian",
  },
  {
    code: "de",
    shortCode: "DE",
    name: "Deutsch",
    englishName: "German",
  },
  {
    code: "zh-CN",
    shortCode: "ZH",
    name: "中文",
    englishName: "Chinese",
  },
  {
    code: "es",
    shortCode: "ES",
    name: "Español",
    englishName: "Spanish",
  },
];

export function getLanguageByCode(code: string) {
  const normalized = code.toLowerCase();
  return (
    SUPPORTED_LANGUAGES.find(
      (l) =>
        l.code.toLowerCase() === normalized ||
        l.shortCode.toLowerCase() === normalized ||
        (normalized === "zh" && l.code === "zh-CN")
    ) || SUPPORTED_LANGUAGES[0]
  );
}

export function getCurrentLanguage(): typeof SUPPORTED_LANGUAGES[0] {
  if (typeof window === "undefined") return SUPPORTED_LANGUAGES[0];

  // 1. Check URL query
  const params = new URLSearchParams(window.location.search);
  const langParam = params.get("lang");
  if (langParam) {
    const matched = getLanguageByCode(langParam);
    if (matched) return matched;
  }

  // 2. Check googtrans cookie
  const match = document.cookie.match(/googtrans=\/en\/([^;]+)/);
  if (match && match[1]) {
    const code = match[1];
    return getLanguageByCode(code);
  }

  // 3. Check localStorage
  const saved = localStorage.getItem("selected_language");
  if (saved) {
    return getLanguageByCode(saved);
  }

  return SUPPORTED_LANGUAGES[0];
}

export function setGoogleTranslateCookie(langCode: string) {
  const isEn = langCode === "en" || langCode === "EN";
  const targetCode = langCode === "zh" ? "zh-CN" : langCode;
  const cookieValue = isEn ? "/en/en" : `/en/${targetCode}`;

  const host = window.location.hostname;
  const domainParts = host.split(".");
  const rootDomain =
    domainParts.length > 2
      ? `.${domainParts.slice(-2).join(".")}`
      : host;

  // Set cookie on current domain and root domain
  document.cookie = `googtrans=${cookieValue}; path=/;`;
  document.cookie = `googtrans=${cookieValue}; path=/; domain=${host};`;
  if (rootDomain !== host) {
    document.cookie = `googtrans=${cookieValue}; path=/; domain=${rootDomain};`;
  }

  localStorage.setItem("selected_language", targetCode);
}

export function switchLanguage(langCode: string) {
  const target = getLanguageByCode(langCode);
  setGoogleTranslateCookie(target.code);

  // Update Google Translate select element if it exists in DOM
  const selectElem = document.querySelector(
    ".goog-te-combo"
  ) as HTMLSelectElement | null;

  if (selectElem) {
    selectElem.value = target.code;
    selectElem.dispatchEvent(new Event("change"));
  }

  // Update URL search parameter
  const url = new URL(window.location.href);
  if (target.code === "en") {
    url.searchParams.delete("lang");
  } else {
    url.searchParams.set("lang", target.shortCode.toLowerCase());
  }

  // Trigger reload or navigation to ensure all dynamic elements update
  window.location.href = url.toString();
}

export default function GoogleTranslateScript() {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  useEffect(() => {
    // Check if lang is provided in URL
    const langParam = searchParams.get("lang");
    if (langParam) {
      const matched = getLanguageByCode(langParam);
      if (matched) {
        setGoogleTranslateCookie(matched.code);
      }
    }

    // Define the global callback function
    window.googleTranslateElementInit = function () {
      if (window.google?.translate?.TranslateElement) {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: "en",
            includedLanguages: "en,fr,it,de,zh-CN,es",
            autoDisplay: false,
          },
          "google_translate_element"
        );
      }
    };

    // Load Google Translate script if not already added
    const existingScript = document.getElementById("google-translate-script");
    if (!existingScript) {
      const script = document.createElement("script");
      script.id = "google-translate-script";
      script.type = "text/javascript";
      script.async = true;
      script.src =
        "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      document.body.appendChild(script);
    }
  }, [searchParams, pathname]);

  return <div id="google_translate_element" className="hidden notranslate" style={{ display: "none" }} />;
}
